export type WebCitationSource = {
  citationKey: string;
  excerpt?: string;
  publishedAt?: string;
  retrievedAt?: string;
  title?: string;
  url: string;
};

export type WebSourcePresentation = Omit<WebCitationSource, 'citationKey'> & {
  citationKey?: string;
};

const CITATION_KEY_VALUE_PATTERN = /^S[1-9]\d*$/;

type LoosePart = { type: string } & Record<string, unknown>;

function readString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

export function safeWebCitationUrl(value: string): string | null {
  try {
    const url = new URL(value);
    if ((url.protocol !== 'https:' && url.protocol !== 'http:') || url.username || url.password) {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
}

function nestedInersiaMetadata(record: Record<string, unknown>) {
  const providerMetadata = record.providerMetadata;
  if (!providerMetadata || typeof providerMetadata !== 'object' || Array.isArray(providerMetadata)) {
    return {};
  }
  const inersia = (providerMetadata as Record<string, unknown>).inersia;
  return inersia && typeof inersia === 'object' && !Array.isArray(inersia)
    ? inersia as Record<string, unknown>
    : {};
}

export function getWebSourcePresentation(record: LoosePart): WebSourcePresentation | null {
  const metadata = nestedInersiaMetadata(record);
  const citationKey = readString(record, 'citationKey') ?? readString(metadata, 'citationKey');
  const rawUrl = readString(record, 'url');
  const url = rawUrl ? safeWebCitationUrl(rawUrl) : null;
  if (!url) {
    return null;
  }

  return {
    ...(citationKey && CITATION_KEY_VALUE_PATTERN.test(citationKey) ? { citationKey } : {}),
    excerpt: readString(record, 'excerpt') ?? readString(metadata, 'excerpt'),
    publishedAt: readString(record, 'publishedAt') ?? readString(metadata, 'publishedAt'),
    retrievedAt: readString(record, 'retrievedAt') ?? readString(metadata, 'retrievedAt'),
    title: readString(record, 'title') ?? readString(metadata, 'title'),
    url,
  };
}

function sourceFromRecord(record: Record<string, unknown>): WebCitationSource | null {
  const source = getWebSourcePresentation({ ...record, type: 'source-url' });
  if (!source?.citationKey) {
    return null;
  }
  return { ...source, citationKey: source.citationKey };
}

/**
 * Collects citable web sources from persisted source-url parts and live
 * tool-fetch_url outputs. Only absolute HTTP(S) URLs can become links.
 */
export function collectWebCitationSources(parts: ReadonlyArray<LoosePart>): WebCitationSource[] {
  const sources = new Map<string, WebCitationSource>();

  for (const part of parts) {
    if (part.type === 'source-url') {
      const source = sourceFromRecord(part);
      if (source && !sources.has(source.citationKey)) {
        sources.set(source.citationKey, source);
      }
      continue;
    }

    if (part.type === 'tool-fetch_url' && part.state === 'output-available') {
      const output = part.output;
      if (!output || typeof output !== 'object' || Array.isArray(output)) {
        continue;
      }
      const record = output as Record<string, unknown>;
      if (record.error === true) {
        continue;
      }

      const source = sourceFromRecord({
        ...record,
        url: readString(record, 'finalUrl') ?? readString(record, 'url'),
      });
      if (source && !sources.has(source.citationKey)) {
        sources.set(source.citationKey, source);
      }
    }
  }

  return [...sources.values()];
}

/** Short, readable site label for a source link (host name without `www.`). */
export function getWebSourceSiteName(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./i, '');
  } catch {
    return url;
  }
}

// Titles of error, bot-wall or interstitial pages: such a fetch proves nothing
// and must not be listed as a consulted source (defensive, the backend filters too).
const BLOCKED_PAGE_TITLE_PATTERNS = [
  /^\s*(?:error\s*)?(?:4\d{2}|5\d{2})\b/i,
  /\b(?:4\d{2}|5\d{2})\s*(?:error|forbidden|not found|unauthorized|too many requests)\b/i,
  /\baccess denied\b/i,
  /\bforbidden\b/i,
  /^\s*just a moment\b/i,
  /\battention required\b/i,
  /\b(?:page|site) not found\b/i,
  /\bpage introuvable\b/i,
  /\baccès refusé/i,
  /\b(?:are you a robot|captcha|verify you are human)\b/i,
  // "Page erreur 404 - Site", "Page d'erreur - 404", "Page error 404", "Page d'erreur", "Page 404";
  // not "Page d'accueil" nor "Page erreur de frappe courante".
  /^\s*page\s+(?:d['’]\s*)?(?:(?:error|erreur)(?:\s*[-–—:|]?\s*[45]\d\d\b|\s*$)|[45]\d\d\s*$)/i,
];

export function isBlockedPageTitle(title: string | undefined) {
  return Boolean(title && BLOCKED_PAGE_TITLE_PATTERNS.some((pattern) => pattern.test(title)));
}

function normalizedSourceTitle(title: string | undefined) {
  return (title ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * One row per consulted page (no grouping by site): pages cited in the answer
 * first, in order of first citation, then the other consulted pages. Exact URL
 * duplicates, error/blocking pages and same-site same-title duplicates (the first one is
 * kept, hence the cited one) are dropped; an untitled row is never merged.
 */
export function orderWebSourcesForList(
  sources: readonly WebSourcePresentation[],
  citedKeys: readonly string[],
): WebSourcePresentation[] {
  const seenUrls = new Set<string>();
  const unique = sources.filter((source) => {
    if (seenUrls.has(source.url) || isBlockedPageTitle(source.title)) return false;
    seenUrls.add(source.url);
    return true;
  });
  const citationRank = (source: WebSourcePresentation) => {
    const rank = source.citationKey ? citedKeys.indexOf(source.citationKey) : -1;
    return rank === -1 ? Number.POSITIVE_INFINITY : rank;
  };
  const ordered = unique
    .map((source, index) => ({ index, rank: citationRank(source), source }))
    .sort((a, b) => (a.rank - b.rank) || (a.index - b.index))
    .map(({ source }) => source);

  // Same site and same normalized title: the same page under two URLs. Display only.
  const seenSiteTitles = new Set<string>();
  return ordered.filter((source) => {
    const title = normalizedSourceTitle(source.title);
    if (!title) return true;
    const key = `${getWebSourceSiteName(source.url).toLowerCase()}\n${title}`;
    if (seenSiteTitles.has(key)) return false;
    seenSiteTitles.add(key);
    return true;
  });
}

/** Readable page address for a row without title: host and path, no scheme. */
export function getWebSourceDisplayAddress(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname === '/' ? '' : parsed.pathname;
    return `${parsed.hostname.replace(/^www\./i, '')}${path}`;
  } catch {
    return url;
  }
}
